import * as React from 'react';
import { deburr, kebabCase } from 'lodash';

import ActionButton from 'components/buttons/action-button/ActionButton';
import LineGraph from 'components/graphs/echarts/line-graph/LineGraphLazy';
import SOVKpiSelector from 'modules/insights/sov/components/analysis/kpis/SOVKpiSelector';

import { __t, applyTranslations } from 'utils/language';
import { downloadIndividualFileWithProxying } from 'utils/file';
import { logError } from 'utils/log';
import { GRAPH_KPI_OPTIONS_INSTAGRAM, GRAPH_KPI_OPTIONS_YOUTUBE } from 'modules/insights/sov/utils/sov';
import { getData } from 'components/graphs/echarts/line-graph/utils/line-graph';

import './sov-graph-over-time.scss';

import type {
  LineGraphDateInterval,
  LineGraphEChartsObject,
  LineGraphEndpointData,
  SOVReportType
} from 'types';

applyTranslations(require('locales/common/collabary-common.i18n.json'));
applyTranslations(require('modules/insights/sov/shared/sov-shared.i18n.json'));
applyTranslations(require('./sov-graph-over-time.i18n.json'));

type Props = {
  results: Array<LineGraphEndpointData>,
  reportName: string,
  reportType: SOVReportType,
  dateInterval: LineGraphDateInterval,
  totalUniqueInfluencers: number,
  chartInstanceCallback?: (a: LineGraphEChartsObject) => void
};

type State = {
  selectedItem: string,
  chartInstance: LineGraphEChartsObject | undefined | null
};

const COMMON_MSG_KEY = 'collabary_common';
const SHARED_MSG_KEY = 'sov_shared';
const MSG_KEY = 'sov_graph_over_time';

class SOVGraphOverTime extends React.Component<Props, State> {
  state: State = {
    selectedItem: this.props.reportType === 'instagram' ? 'impressions' : 'views',
    chartInstance: null
  };

  shouldComponentUpdate(nextProps: Props, nextState: State) {
    const { reportType } = this.props;
    const { selectedItem } = this.state;

    return reportType !== nextProps.reportType
      || selectedItem !== nextState.selectedItem;
  }

  handleSelection = (
    value: string
  ): void => {
    this.setState(() => ({
      selectedItem: value
    }));
  };

  handleChartInstance = (instance: LineGraphEChartsObject): void => {
    const { chartInstanceCallback } = this.props;

    if (chartInstanceCallback) {
      chartInstanceCallback(instance);
      this.setState({ chartInstance: instance });
    }
  };

  handleChartDownload = (): void => {
    const { reportName, reportType } = this.props;
    const { chartInstance } = this.state;

    if (chartInstance) {
      const safeReportName = deburr(`${reportName}-${reportType}`)
        .replace(/[^a-zA-Z0-9.-_]/g, '_');
      const fileName = `${kebabCase(safeReportName)}.png`;

      downloadIndividualFileWithProxying(chartInstance.getDataURL({ backgroundColor: '#fff' }), fileName)
        .catch(error => logError(error));
    }
  };

  renderSelection(): React.ReactNode {
    const { reportType } = this.props;
    const { selectedItem } = this.state;
    let kpiOptions: Record<string, string> = GRAPH_KPI_OPTIONS_INSTAGRAM;

    if (reportType === 'youtube') {
      kpiOptions = GRAPH_KPI_OPTIONS_YOUTUBE;
    }

    const dropdownOptions = Object.keys(kpiOptions).map(key => ({
      label: __t(`${COMMON_MSG_KEY}.kpis.${kpiOptions[key]}`),
      value: key
    }));

    return (
      <SOVKpiSelector
        label={__t(`${SHARED_MSG_KEY}.labels.select_kpi`)}
        options={dropdownOptions}
        value={selectedItem}
        onChange={this.handleSelection}
      />
    );
  }

  render(): React.ReactNode {
    const { results, reportName, reportType, dateInterval, totalUniqueInfluencers } = this.props;
    const { selectedItem } = this.state;
    const lineGraphData = getData(results, selectedItem, reportType, reportName, dateInterval, totalUniqueInfluencers);

    return (
      <section className="sov-graph-over-time">
        <div className="sov-graph-over-time__header">
          <div className="sov-graph-over-time__title-section">
            <div className="sov-graph-over-time__title">
              {__t(`${MSG_KEY}.label_development_over_time`)}
            </div>
            <div className="sov-graph-over-time__subtitle">
              {__t(`${MSG_KEY}.label_help_note`)}
            </div>
          </div>
          <ActionButton onClick={this.handleChartDownload}>
            {__t(`${COMMON_MSG_KEY}.action_button.download`)}
          </ActionButton>
        </div>
        <div className="sov-kpi-selector">
          {this.renderSelection()}
        </div>
        <div className="sov-graph-over-time__row">
          <div
            className="sov-graph-over-time__graph"
            style={{ height: 560 }}
          >
            <LineGraph
              key={selectedItem}
              data={lineGraphData}
              chartInstanceCallback={this.handleChartInstance}
            />
          </div>
        </div>
      </section>
    );
  }
}

export default SOVGraphOverTime;
